import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { isRequiresAttentionStatus } from '../../../Utils/purchaseInvoices/statusTranslations';
import useSpInvoices from "../pages/supplierInvoiceFilesHook";
import { exportToExcel } from "../../../Utils/ExportToExcel";
import { excelColumns, INVOICE_SORT_FIELDS } from '../../../constants/purchaseInvoices/invoiceManagement/invoiceTableConstants';
import { useQueryClient } from '@tanstack/react-query';
import useAdminStatus from "../../../hooks/useAdminStatus";
import {
    INVOICES_QUERY_KEY,
    INVOICE_STATUS_CHANGED_EVENT,
    INVOICE_APPROVER_TRANSFERRED_EVENT,
    INVOICE_STATUS_PROCESSING_HE,
    INVOICE_STATUS_PENDING_GOODS_RECEIPT,
    OPTIMISTIC_UPDATE_TTL_MS,
    HIGHLIGHT_SCROLL_COMPLETE_DELAY_MS,
    STATUS_CHANGE_TABLE_SYNC_DELAY_MS,
    POST_ACTION_REFRESH_DELAY_MS,
    CHECK_RESULT_SUCCESS_CLEAR_MS,
    CHECK_RESULT_STATUS_CLEAR_MS,
    CHECK_RESULT_PARTIAL_CLEAR_MS,
    CHECK_RESULT_ERROR_CLEAR_MS,
    HEBREW_INVOICE_MESSAGES,
} from '../../../constants/purchaseInvoices/supplierInvoicesConstants';
import logger from '../../../logger';

const useInvoiceTable = ({ onHighlightComplete, highlightedInvoiceId }) => {
    const isOverdueAttention = useCallback((invoice) => {
        if (!isRequiresAttentionStatus(invoice.status)) {
            return false;
        }
        const attentionStartDate = invoice.errorDetails?.timestamp
            ? new Date(invoice.errorDetails.timestamp)
            : (invoice.uploadedAt ? new Date(invoice.uploadedAt) : new Date());
        const now = new Date();
        const daysPassed = Math.floor((now - attentionStartDate) / (1000 * 60 * 60 * 24));
        const threshold = invoice.groupSettings?.requiresAttentionDaysThreshold || 3;
        return daysPassed > threshold;
    }, []);

    const [openSearch, setOpenSearch] = useState(false);
    const [activeSearches, setActiveSearches] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [globalSearchTerm, setGlobalSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState(INVOICE_SORT_FIELDS.UPLOADED_AT);
    const [order, setOrder] = useState('desc');
    const [openEdit, setOpenEdit] = useState(false);
    const [editInvoice, setEditInvoice] = useState(null);
    const [checkingBooknum, setCheckingBooknum] = useState(null);
    const [checkResult, setCheckResult] = useState({});
    const [openInvoicePopup, setOpenInvoicePopup] = useState(false);
    const [selectedInvoiceForPopup, setSelectedInvoiceForPopup] = useState(null);
    const [approvalPackageForPopup, setApprovalPackageForPopup] = useState(null);
    const [pendingInvoiceUpdates, setPendingInvoiceUpdates] = useState({});
    const [isReprocessing, setIsReprocessing] = useState(false);

    const highlightedInvoiceRef = useRef(null);
    const queryClient = useQueryClient();
    const { isAdmin } = useAdminStatus();

    const spInvoicesHook = useSpInvoices();
    const {
        openDelete,
        setOpenDelete,
        deleteMutation,
        updateMutation,
        fileAsBase64,
        isLoading,
        isLoadingBackground,
        isLoadingFile,
        errorFile,
        setSelectedFileName,
        selectedFileName,
        statuses,
        selectedStatus,
        setSelectedStatus,
        filteredspInvoices,
        spInvoices,
        refreshData,
        checkGoodsReceiptNow,
        showToast,
        handleSaveInvoiceNotes,
        handleApproveInvoiceFromPopup,
        handleRejectInvoiceFromPopup,
        handleRowClick,
        initiateApproval,
        completeApprovalWithAllocationKey,
        allocationKeyModalOpen,
        setAllocationKeyModalOpen,
        allocationKeys,
        isLoadingAllocationKeys,
        allocationKeysError,
        currentInvoiceYear,
        isInvoicePriceHigher,
        retryApprovedInvoiceToPriority,
        retryRequiresAttentionInvoiceToPriority,
        changeInvoiceStatus,
        reprocessRequiresAttentionInvoice,
        setActiveInvoicesData
    } = spInvoicesHook;
    const { accountantNamesCache, getAccountantDisplayName } = spInvoicesHook;

    const updateInvoiceOptimistically = useCallback((invoiceId, updates) => {
        setPendingInvoiceUpdates(prev => ({
            ...prev,
            [invoiceId]: { ...prev[invoiceId], ...updates }
        }));
        setTimeout(() => {
            setPendingInvoiceUpdates(prev => {
                const newUpdates = { ...prev };
                delete newUpdates[invoiceId];
                return newUpdates;
            });
        }, OPTIMISTIC_UPDATE_TTL_MS);
    }, []);

    const getUpdatedInvoice = useCallback((invoice) => {
        if (!invoice) return null;
        const pendingUpdates = pendingInvoiceUpdates[invoice.id];
        return pendingUpdates ? { ...invoice, ...pendingUpdates } : invoice;
    }, [pendingInvoiceUpdates]);

    const canDeleteInvoice = useCallback((invoice) => {
        const forbiddenStatuses = [
            'אושר ונקלט',
            'CANCELLED',
            'DRAFT_IN_PRIORITY',
            'received',
            'סופית',
        ];
        const status = invoice.status;

        const isForbidden = forbiddenStatuses.some(forbiddenStatus =>
            status && (
                status === forbiddenStatus ||
                status.toLowerCase() === forbiddenStatus.toLowerCase() ||
                status.toUpperCase() === forbiddenStatus.toUpperCase()
            )
        );

        return !isForbidden;
    }, []);

    const onTransferApproverOptimistic = useCallback(async (transferData, newApproverName) => {
        updateInvoiceOptimistically(transferData.invoiceId, {
            approvalPackage: {
                ...selectedInvoiceForPopup?.approvalPackage,
                currentApprover: newApproverName,
                canApprove: false
            }
        });

        return transferData;
    }, [selectedInvoiceForPopup, updateInvoiceOptimistically]);

    const completeApprovalWithAllocationKeyAndClose = useCallback((selectedKey) => {
        return completeApprovalWithAllocationKey(selectedKey, () => setOpenInvoicePopup(false));
    }, [completeApprovalWithAllocationKey, setOpenInvoicePopup]);

    useEffect(() => {
        if (highlightedInvoiceId && highlightedInvoiceRef.current) {
            highlightedInvoiceRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            const timer = setTimeout(() => {
                if (onHighlightComplete) {
                    onHighlightComplete();
                }
            }, HIGHLIGHT_SCROLL_COMPLETE_DELAY_MS);
            return () => clearTimeout(timer);
        }
    }, [highlightedInvoiceId, onHighlightComplete]);

  
    useEffect(() => {
        if (highlightedInvoiceId && filteredspInvoices?.length > 0 && !openInvoicePopup) {
            const targetInvoice = filteredspInvoices.find(inv => 
                inv.id === highlightedInvoiceId || inv._id === highlightedInvoiceId
            );
            if (targetInvoice) {
                handleRowClick(targetInvoice, {
                    setSelectedInvoiceForPopup,
                    setApprovalPackageForPopup,
                    setOpenInvoicePopup
                });
            }
        }
    }, [highlightedInvoiceId, filteredspInvoices, openInvoicePopup, handleRowClick]);


    useEffect(() => {
        const handleStatusChange = (event) => {
            if (event.detail?.invoiceId && event.detail?.newStatus) {
                const applyUpdate = (data) =>
                    (data || []).map(inv =>
                        inv.id === event.detail.invoiceId
                            ? { ...inv, status: event.detail.newStatus }
                            : inv
                    );
                queryClient.setQueryData(
                    [INVOICES_QUERY_KEY, 'all'],
                    applyUpdate(queryClient.getQueryData([INVOICES_QUERY_KEY, 'all']))
                );
                queryClient.setQueryData(
                    [INVOICES_QUERY_KEY, 'priority'],
                    applyUpdate(queryClient.getQueryData([INVOICES_QUERY_KEY, 'priority']))
                );
            }

            setTimeout(() => {
                queryClient.invalidateQueries({
                    queryKey: [INVOICES_QUERY_KEY, 'all'],
                    refetchType: 'none'
                });
            }, STATUS_CHANGE_TABLE_SYNC_DELAY_MS);
        };

        const handleApproverTransfer = (event) => {
            if (event.detail?.invoiceId && event.detail?.newApproverName) {
                updateInvoiceOptimistically(event.detail.invoiceId, {
                    approvalPackage: {
                        ...selectedInvoiceForPopup?.approvalPackage,
                        currentApprover: event.detail.newApproverName,
                        canApprove: false
                    }
                });
            }
        };

        window.addEventListener(INVOICE_STATUS_CHANGED_EVENT, handleStatusChange);
        window.addEventListener(INVOICE_APPROVER_TRANSFERRED_EVENT, handleApproverTransfer);

        return () => {
            window.removeEventListener(INVOICE_STATUS_CHANGED_EVENT, handleStatusChange);
            window.removeEventListener(INVOICE_APPROVER_TRANSFERRED_EVENT, handleApproverTransfer);
        };
    }, [queryClient, updateInvoiceOptimistically, selectedInvoiceForPopup]);

    useEffect(() => {
        if (openInvoicePopup && selectedInvoiceForPopup) {
            const updatedInvoice = filteredspInvoices.find(inv => inv.id === selectedInvoiceForPopup.id);
            if (updatedInvoice) {
                const finalInvoice = getUpdatedInvoice(updatedInvoice);
                setSelectedInvoiceForPopup(finalInvoice);
            }
        }
    }, [filteredspInvoices, openInvoicePopup, selectedInvoiceForPopup, getUpdatedInvoice]);

    const onRowClick = (invoice) => {
        handleRowClick(invoice, {
            setSelectedInvoiceForPopup,
            setApprovalPackageForPopup,
            setOpenInvoicePopup
        });
    };

    const onSaveNotes = (invoice, notes, isReplace = false) => handleSaveInvoiceNotes(invoice, notes, isReplace);

    const onApproveInvoice = (invoiceId, allocationKeyName) =>
        handleApproveInvoiceFromPopup(invoiceId, allocationKeyName, () => setOpenInvoicePopup(false));

    const onRejectInvoice = (invoiceId, rejectionData) =>
        handleRejectInvoiceFromPopup(invoiceId, rejectionData, () => setOpenInvoicePopup(false));

    const handleCheckGoodsReceipt = async (id) => {
        if (checkingBooknum === id) {
            return;
        }

        setCheckingBooknum(id);
        setCheckResult({});
        try {
            const res = await checkGoodsReceiptNow(id);

            if (res?.result?.status === 'COMPLETED') {
                setActiveInvoicesData(spInvoices.map(inv =>
                    inv.id === id ? { ...inv, status: INVOICE_STATUS_PROCESSING_HE } : inv
                ));
                setCheckResult({ [id]: { success: true, message: res.message } });
                showToast(res.message, "success");
                setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
                    setCheckResult({});
                }, CHECK_RESULT_SUCCESS_CLEAR_MS);
            }
            else if (res?.result?.status === 'PARTIAL' && res?.result?.ordersFound > 0) {
                setCheckResult({ [id]: { success: false, message: res.message } });
                showToast(res.message, "warning");
                setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
                    setCheckResult({});
                }, CHECK_RESULT_PARTIAL_CLEAR_MS);
            }
            else if (res?.result?.status === 'NO_ADDITIONAL_ORDERS') {
                setCheckResult({ [id]: { success: false, message: res.message } });
                showToast(res.message, "info");
                setTimeout(() => setCheckResult({}), CHECK_RESULT_STATUS_CLEAR_MS);
            }
            else if (res?.receivedGoods) {
                setActiveInvoicesData(spInvoices.map(inv =>
                    inv.id === id ? { ...inv, status: INVOICE_STATUS_PROCESSING_HE } : inv
                ));
                setCheckResult({ [id]: { success: true, message: "הסחורה התקבלה בהצלחה!" } });
                showToast("הסחורה התקבלה בהצלחה! החשבונית עברה לתהליך אישור", "success");
                setTimeout(() => {
                    queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
                    setCheckResult({});
                }, CHECK_RESULT_SUCCESS_CLEAR_MS);
            } 
            else if (res?.result?.partial) {
                setCheckResult({ [id]: { success: false, message: res.result.message } });
                showToast(res.result.message || "קבלת סחורה חלקית - ממתין לקבלות נוספות", "warning");
                setTimeout(() => setCheckResult({}), CHECK_RESULT_ERROR_CLEAR_MS);
            } 
            else if (res?.result?.alreadyBilled) {
                setCheckResult({ [id]: { success: false, message: res.result.message } });
                showToast(res.result.message || "קבלת הסחורה כבר חויבה במערכת פריוריטי", "warning");
                setTimeout(() => setCheckResult({}), CHECK_RESULT_ERROR_CLEAR_MS);
            } 
            else {
                const displayMessage = res?.result?.message || res?.message || "הבדיקה בוצעה: הסחורה עדיין לא התקבלה.";
                const isNotFound = displayMessage.includes('not found') || displayMessage.includes('לא נמצאה');
                const isCancelled = displayMessage.includes('cancelled') || displayMessage.includes('בוטלה');
                const isCooldown = displayMessage.includes('wait') || displayMessage.includes('30 seconds');
                
                let hebrewMessage = displayMessage;
                let toastType = "info";

                if (displayMessage === 'Invoice not found') {
                    hebrewMessage = HEBREW_INVOICE_MESSAGES.INVOICE_NOT_FOUND;
                    toastType = "error";
                } else if (displayMessage === 'Invoice has been cancelled') {
                    hebrewMessage = HEBREW_INVOICE_MESSAGES.INVOICE_CANCELLED;
                    toastType = "warning";
                } else if (displayMessage.includes('wait 30 seconds') || displayMessage.includes('performed recently')) {
                    hebrewMessage = HEBREW_INVOICE_MESSAGES.WAIT_30_SECONDS;
                    toastType = "warning";
                } else if (displayMessage === 'No goods receipt found yet') {
                    hebrewMessage = HEBREW_INVOICE_MESSAGES.NO_GOODS_RECEIPT;
                    toastType = "warning";
                }

                setCheckResult({ [id]: { success: false, message: hebrewMessage } });
                showToast(hebrewMessage, toastType);
                setTimeout(() => setCheckResult({}), CHECK_RESULT_ERROR_CLEAR_MS);
            }
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || "שגיאה בבדיקה";
            setCheckResult({ [id]: { success: false, message: errorMessage } });
            showToast(errorMessage, "error");
            setTimeout(() => setCheckResult({}), CHECK_RESULT_ERROR_CLEAR_MS);
        } finally {
            setCheckingBooknum(null);
        }
    };

    const handleRetryApprovedInvoice = async (invoiceId) => {
        try {
            showToast("שולח חשבונית מחדש לפריוריטי...", "info");
            const result = await retryApprovedInvoiceToPriority(invoiceId);

            if (result?.status === 'SUCCESS') {
                showToast("החשבונית נשלחה מחדש בהצלחה", "success");
                setTimeout(() => {
                    refreshData();
                }, POST_ACTION_REFRESH_DELAY_MS);
            } else {
                throw new Error(result?.message || "שגיאה בשליחה מחדש");
            }
        } catch (error) {
            logger.error('Error retrying approved invoice', { invoiceId, error: error.message });
            const errorMessage = error.response?.data?.message || error.message || "שגיאה בשליחה חוזרת לפריוריטי";
            showToast(errorMessage, "error");
        }
    };

    const handleRetryRequiresAttentionInvoice = async (invoiceId) => {
        try {
            showToast("שולח חשבונית מחדש לפריוריטי...", "info");
            const result = await retryRequiresAttentionInvoiceToPriority(invoiceId);

            if (result?.status === 'SUCCESS') {
                showToast("החשבונית נשלחה מחדש בהצלחה", "success");
                setTimeout(() => {
                    refreshData();
                }, POST_ACTION_REFRESH_DELAY_MS);
            } else {
                throw new Error(result?.message || "שגיאה בשליחה מחדש");
            }
        } catch (error) {
            logger.error('Error retrying requires-attention invoice', { invoiceId, error: error.message });
            const errorMessage = error.response?.data?.message || error.message || "שגיאה בשליחה חוזרת לפריוריטי";
            showToast(errorMessage, "error");
        }
    };

    const handleChangeInvoiceStatus = async (invoiceId, newStatus) => {
        try {
            showToast("משנה סטטוס החשבונית...", "info");
            const result = await changeInvoiceStatus(invoiceId, newStatus);

            if (result?.status === 'SUCCESS') {
                showToast("סטטוס החשבונית שונה בהצלחה", "success");
                setTimeout(() => {
                    refreshData();
                }, POST_ACTION_REFRESH_DELAY_MS);
            } else {
                throw new Error(result?.message || "שגיאה בשינוי סטטוס");
            }
        } catch (error) {
            console.error('Error changing invoice status:', error);
            const errorMessage = error.response?.data?.message || error.message || "שגיאה בשינוי סטטוס החשבונית";
            showToast(errorMessage, "error");
        }
    };

    const handleReprocessAttentionInvoice = async (invoiceId, updatedFields) => {
        try {
            showToast("מעבד מחדש את החשבונית...", "info");
            const result = await reprocessRequiresAttentionInvoice(invoiceId, updatedFields);

            if (result?.status === 'SUCCESS') {
                showToast("החשבונית תוקנה ונשלחה לאישור בהצלחה", "success");
            } else if (result?.status === 'PENDING') {
                showToast("החשבונית תוקנה ומחכה לקבלת סחורה", "info");
            } else if (result?.status === 'REQUIRES_ATTENTION') {
                showToast("החשבונית עדיין דורשת תיקון נוסף", "warning");
            }

            setTimeout(() => {
                refreshData();
            }, POST_ACTION_REFRESH_DELAY_MS);

            return result;
        } catch (error) {
            console.error('Error reprocessing attention invoice:', error);
            const errorMessage = error.response?.data?.message || error.message || "שגיאה בעיבוד מחדש של החשבונית";
            showToast(errorMessage, "error");
            throw error;
        }
    };

    const handleAfterDelete = useCallback(() => {
        setTimeout(() => {
            if (selectedStatus !== "all" && filteredspInvoices.length === 0) {
                setSelectedStatus("all");
                setGlobalSearchTerm('');
                setActiveSearches({});
                setFilteredData([]);
            }
        }, 500);
    }, [selectedStatus, filteredspInvoices.length, setSelectedStatus]);

    const handleDeleteInvoice = (fileName) => {
        deleteMutation.mutate(fileName, {
            onSuccess: () => {
                setSelectedFileName(null);
                setOpenDelete(false);
                showToast("החשבונית נמחקה בהצלחה", "success");
                refreshData();
                handleAfterDelete();
            }
        });
    };

    const handleGlobalSearch = (searchTerm) => {
        setGlobalSearchTerm(searchTerm);
        setPage(0);
        if (searchTerm?.trim() && Object.keys(activeSearches).length > 0) {
            setActiveSearches({});
            setFilteredData([]);
        }
    };

    const handleSearch = (searchValues, isDelete = false) => {
        setOpenSearch(false);
        setPage(0);

        const nonEmptySearches = Object.fromEntries(
            Object.entries(searchValues).filter(([_, value]) => value?.toString().trim())
        );

        const updatedSearches = isDelete ? nonEmptySearches : {
            ...activeSearches,
            ...nonEmptySearches
        };

        setActiveSearches(updatedSearches);
        if (!Object.keys(updatedSearches).length) {
            setFilteredData([]);
            return;
        }

        const dateToComparable = (dateStr) => {
            if (!dateStr) return '';
            if (dateStr.includes('/')) {
                const [day, month, year] = dateStr.split('/');
                return `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`;
            }
            return dateStr.replace(/-/g, '');
        };

        const filtered = spInvoices.filter(item => {
            return Object.entries(updatedSearches).every(([key, value]) => {
                if (key === 'fromDate' || key === 'toDate') {
                    const itemDate = dateToComparable(item.date);
                    const searchDate = dateToComparable(value);

                    return key === 'fromDate'
                        ? itemDate >= searchDate
                        : itemDate <= searchDate;
                }

                if (key === 'accountManager') {
                    const accManager = item.approvalPackage?.accountManager || '';
                    const displayName = accountantNamesCache[accManager] || accManager;
                    return displayName.toString().toLowerCase().includes(value.toString().toLowerCase());
                }

                return item[key]?.toString().toLowerCase().includes(value.toString().toLowerCase());
            });
        });

        setFilteredData(filtered);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const applyGlobalSearch = useCallback((data, searchTerm) => {
        if (!searchTerm?.trim()) return data;

        const term = searchTerm.toLowerCase();
        return data.filter(invoice => {
            const fieldsToSearchIn = [
                'id',
                'booknum',
                'supplierName',
                'date',
                'amount',
                'status',
                'approver',
                'fileName',
                'notes',
                'accountManager'
            ];
            return fieldsToSearchIn.some(field => {
                if (field === 'accountManager') {
                    const accManager = invoice.approvalPackage?.accountManager || '';
                    const displayName = accountantNamesCache[accManager] || accManager;
                    return displayName.toString().toLowerCase().includes(term);
                }
                return invoice[field]?.toString().toLowerCase().includes(term);
            });
        });
    }, []);

    const sortedData = useMemo(() => {
        let baseData;

        const hasAdvancedSearch = Object.keys(activeSearches).length > 0;
        const hasGlobalSearch = globalSearchTerm?.trim();

        if (hasAdvancedSearch) {
            baseData = filteredData;
        } else if (hasGlobalSearch) {
            baseData = spInvoices;
        } else {
            baseData = filteredspInvoices;
        }

        if (!baseData) return [];

        let finalData = hasGlobalSearch
            ? applyGlobalSearch(baseData, globalSearchTerm)
            : baseData;
        if (hasGlobalSearch && selectedStatus !== "all") {
            finalData = finalData.filter(invoice => invoice.status === selectedStatus);
        }

        return [...finalData].sort((a, b) => {
            const isAsc = order === 'asc';

            const aValue = a[orderBy];
            const bValue = b[orderBy];

            if (aValue == null || aValue === '') return isAsc ? 1 : -1;
            if (bValue == null || bValue === '') return isAsc ? -1 : 1;

            if (orderBy === INVOICE_SORT_FIELDS.DATE) {
                if (a.dateForSorting && b.dateForSorting) {
                    return isAsc
                        ? a.dateForSorting - b.dateForSorting
                        : b.dateForSorting - a.dateForSorting;
                }

                const parseDateDDMMYYYY = (dateStr) => {
                    if (!dateStr) return new Date(0);
                    const parts = dateStr.split('/');
                    if (parts.length !== 3) return new Date(dateStr);
                    return new Date(parts[2], parts[1] - 1, parts[0]);
                };

                const dateA = parseDateDDMMYYYY(aValue);
                const dateB = parseDateDDMMYYYY(bValue);

                return isAsc ? dateA - dateB : dateB - dateA;
            }

            if (orderBy === INVOICE_SORT_FIELDS.UPLOADED_AT) {
                if (a.uploadedAtForSorting && b.uploadedAtForSorting) {
                    return isAsc
                        ? a.uploadedAtForSorting - b.uploadedAtForSorting
                        : b.uploadedAtForSorting - a.uploadedAtForSorting;
                }

                const getUploadedAtValue = (item) => {
                    if (item.uploadedAt) {
                        return new Date(item.uploadedAt).getTime();
                    }
                    if (item.date) {
                        const parts = item.date.split('/');
                        if (parts.length === 3) {
                            return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
                        }
                    }
                    return 0;
                };

                const timeA = getUploadedAtValue(a);
                const timeB = getUploadedAtValue(b);

                return isAsc ? timeA - timeB : timeB - timeA;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return isAsc
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            const numA = Number(aValue) || 0;
            const numB = Number(bValue) || 0;

            return isAsc
                ? numA - numB
                : numB - numA;
        });
    }, [filteredspInvoices, filteredData, activeSearches, globalSearchTerm, orderBy, order, applyGlobalSearch, spInvoices, selectedStatus]);

    const paginatedData = useMemo(() => {
        return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const handleEditClick = (invoice) => {
        setEditInvoice(invoice);
        setOpenEdit(true);
    };

    const handleSaveEdit = (updatedInvoice) => {
        updateMutation.mutate(updatedInvoice, {
            onSuccess: () => {
                setOpenEdit(false);
                setEditInvoice(null);
            }
        });
    };

    const handleReprocessAttention = async (updatedInvoice) => {
        setIsReprocessing(true);
        try {
            const invoiceId = updatedInvoice._id || updatedInvoice.id;
            const updatedFields = {
                companyId: updatedInvoice.companyId,
                companyName: updatedInvoice.companyName,
                supplierName: updatedInvoice.supplierName,
                booknum: updatedInvoice.booknum,
                date: updatedInvoice.date,
                amount: updatedInvoice.amount,
                allocationNumber: updatedInvoice.allocationNumber
            };

            const result = await reprocessRequiresAttentionInvoice(invoiceId, updatedFields);

            if (result?.status === 'SUCCESS') {
                setActiveInvoicesData(spInvoices.map(inv =>
                    inv.id === invoiceId ? { ...inv, status: INVOICE_STATUS_PROCESSING_HE } : inv
                ));
                showToast("החשבונית תוקנה ונשלחה לאישור בהצלחה", "success");
                setOpenEdit(false);
                setEditInvoice(null);
                queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY, 'all'] });
            } else if (result?.status === 'PENDING') {
                setActiveInvoicesData(spInvoices.map(inv =>
                    inv.id === invoiceId ? { ...inv, status: INVOICE_STATUS_PENDING_GOODS_RECEIPT } : inv
                ));
                showToast("החשבונית תוקנה ומחכה לקבלת סחורה", "info");
                setOpenEdit(false);
                setEditInvoice(null);
                queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY, 'all'] });
            } else if (result?.status === 'REQUIRES_ATTENTION') {
                showToast("החשבונית תוקנה אך עדיין מחכה לתשומת לב", "warning");
                setOpenEdit(false);
                setEditInvoice(null);
                queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY, 'all'] });
            } else {
                throw new Error(result?.message || "שגיאה בעיבוד מחדש של החשבונית");
            }
        } catch (error) {
            console.error('Error reprocessing invoice:', error);
            const errorMessage = error.response?.data?.message || error.message || "שגיאה בעיבוד מחדש של החשבונית";
            showToast(errorMessage, "error");
            throw error;
        } finally {
            setIsReprocessing(false);
        }
    };

    const handleExportToExcel = () => {
        exportToExcel(sortedData, excelColumns, "חשבוניות_רכש");
    };

    const hasActiveSearches = Object.keys(activeSearches).length > 0;
    const hasGlobalSearch = Boolean(globalSearchTerm?.trim());
    const hasAnySearch = hasActiveSearches || hasGlobalSearch;
    const hasNoSearchResults = hasAnySearch && sortedData.length === 0;

    return {
        isOverdueAttention,
        openSearch,
        setOpenSearch,
        activeSearches,
        setActiveSearches,
        filteredData,
        globalSearchTerm,
        page,
        rowsPerPage,
        orderBy,
        order,
        openEdit,
        setOpenEdit,
        editInvoice,
        checkingBooknum,
        checkResult,
        openInvoicePopup,
        setOpenInvoicePopup,
        selectedInvoiceForPopup,
        approvalPackageForPopup,
        highlightedInvoiceRef,
        openDelete,
        setOpenDelete,
        deleteMutation,
        updateMutation,
        fileAsBase64,
        isLoading,
        isLoadingBackground,
        isLoadingFile,
        errorFile,
        setSelectedFileName,
        selectedFileName,
        statuses,
        selectedStatus,
        setSelectedStatus,
        sortedData,
        paginatedData,
        allocationKeyModalOpen,
        setAllocationKeyModalOpen,
        allocationKeys,
        isLoadingAllocationKeys,
        allocationKeysError,
        currentInvoiceYear,
        hasActiveSearches,
        hasGlobalSearch,
        hasAnySearch,
        hasNoSearchResults,
        updateInvoiceOptimistically,
        onTransferApproverOptimistic,
        onRowClick,
        onSaveNotes,
        onApproveInvoice,
        onRejectInvoice,
        handleCheckGoodsReceipt,
        handleRetryApprovedInvoice,
        handleRetryRequiresAttentionInvoice,
        handleChangeInvoiceStatus,
        handleReprocessAttentionInvoice,
        handleGlobalSearch,
        handleSearch,
        handleRequestSort,
        handleEditClick,
        handleSaveEdit,
        handleReprocessAttention,
        handleExportToExcel,
        initiateApproval,
        completeApprovalWithAllocationKey: completeApprovalWithAllocationKeyAndClose,
        isInvoicePriceHigher,
        isAdmin,
        setPage,
        setRowsPerPage,
        isReprocessing,
        handleDeleteInvoice,
        handleAfterDelete,
        canDeleteInvoice,
        canDeleteInvoice,
        getAccountantDisplayName
    };
};

export default useInvoiceTable;